import React, { useState, useEffect, useCallback } from "react";
import { X, CheckCircle, XCircle, Copy, Image, Loader2, Check, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, ArrowRight, ChevronDown, MessageSquare, Send, Paperclip } from "lucide-react";
import toast from "react-hot-toast";
import { useAddIssueNote, useSaveDeveloperNotes } from "../../hooks/issueTriage/useIssueReports";
import { useAuth } from "../../contexts/AuthContext";
import { moveTargets } from "../../lib/issueTriage/triageMoves";
import type { IssuePerson, IssueReport, IssueStatus } from "../../types/issue-triage";
import { ISSUE_SEVERITY_CONFIG, ISSUE_STATUS_CONFIG } from "../../types/issue-triage";
import { formatDistanceToNow } from "date-fns";
import { buildCopyForClaudeText } from "../../lib/issueTriage/formatForClaude";
import { signedInAsIssuePerson } from "../../lib/issueTriage/issuePerson";
import { OwnerPicker } from "./OwnerPicker";
import { IssueFilesPanelView, reportFileOutcomes } from "./IssueFilesPanel";
import { useIssueFileUpload } from "../../hooks/issueTriage/useIssueFiles";

interface IssueDetailPanelProps {
  issue: IssueReport | null;
  allIssues?: IssueReport[]; // All issues for navigation
  onClose: () => void;
  /** liveDeveloperNotes = the Developer Direction textarea's current value,
   * so approve persists what the user sees, saved or not (#4304). */
  onApprove: (id: string, liveDeveloperNotes?: string) => Promise<void> | void;
  onDismiss: (id: string, reason: "not_a_bug" | "duplicate" | "wont_fix" | "feature_request") => void;
  onStatusChange: (id: string, status: IssueStatus) => void;
  onNavigate?: (issue: IssueReport) => void; // Navigate to another issue
  /** Assignable developers; with onOwnerChange the panel shows an owner picker. */
  owners?: IssuePerson[];
  onOwnerChange?: (id: string, owner: IssuePerson | null) => void;
  /**
   * Pickup CLAIM for Copy & Start: resolves true once this user holds the
   * issue (server-checked against the status this panel saw). Copy happens
   * only after it resolves true.
   */
  onPickUp?: (issue: IssueReport) => Promise<boolean>;
  /** "modal" (default) floats over the board; "page" is the issue's own page (/admin/issue-triage/<code>). */
  variant?: "modal" | "page";
  /** True while a dialog (move to Reviewed, confirm dismiss) is open over the panel: letter and arrow keys are then the dialog's, not shortcuts. */
  suspendShortcuts?: boolean;
}

export function IssueDetailPanel({
  issue,
  allIssues = [],
  onClose,
  onApprove,
  onDismiss,
  onStatusChange,
  onNavigate,
  owners,
  onOwnerChange,
  onPickUp,
  variant = "modal",
  suspendShortcuts = false,
}: IssueDetailPanelProps) {
  const pageMode = variant === "page";
  const [copied, setCopied] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [imageZoom, setImageZoom] = useState<number | null>(null);
  const imageContainerRef = React.useRef<HTMLDivElement>(null);
  const imageRef = React.useRef<HTMLImageElement>(null);
  const [showImageFullscreen, setShowImageFullscreen] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [developerNotes, setDeveloperNotes] = useState(issue?.developerNotes || "");
  const [noteText, setNoteText] = useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  // ONE upload loop for the paperclip and the files panel below (they share the busy flag, so two can never run at once).
  const uploader = useIssueFileUpload(issue ?? { id: "" });
  const { upload: uploadFiles, busy: uploading } = uploader;

  const { user } = useAuth();
  const addNote = useAddIssueNote();
  const saveNotes = useSaveDeveloperNotes();
  const isSavingNotes = saveNotes.isPending;

  // Seed the textarea when navigating to a different issue. Deliberately NOT
  // keyed on issue?.developerNotes: the issue prop is now live cache data, and
  // a failed save's rollback (or a background refetch) re-arriving through
  // that dep would clobber the user's in-progress draft.
  useEffect(() => {
    setDeveloperNotes(issue?.developerNotes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.id]);

  const handleSaveNotes = useCallback(async () => {
    // isPending guard: the Save button is disabled while saving, but the
    // Ctrl/Cmd+Enter shortcut is not — don't stack overlapping mutations.
    if (!issue || saveNotes.isPending) return;
    try {
      await saveNotes.mutateAsync({ issueId: issue.id, developerNotes });
      toast.success("Developer notes saved");
    } catch (err) {
      toast.error("Failed to save notes");
    }
  }, [issue, developerNotes, saveNotes]);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const container = imageContainerRef.current;
    if (!container || !img.naturalWidth) return;

    const containerWidth = container.clientWidth - 32;
    const fitZoom = containerWidth / img.naturalWidth;
    setImageZoom(Math.min(Math.max(fitZoom, 0.5), 2));
  }, []);

  useEffect(() => {
    setImageZoom(null);
  }, [issue?.id]);

  useEffect(() => {
    const handleResize = () => {
      const img = imageRef.current;
      const container = imageContainerRef.current;
      if (!img || !container || !img.naturalWidth) return;
      const containerWidth = container.clientWidth - 32;
      const fitZoom = containerWidth / img.naturalWidth;
      setImageZoom(Math.min(Math.max(fitZoom, 0.5), 2));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Navigate all issues, not just needs_review
  const currentIndex = issue ? allIssues.findIndex(i => i.id === issue.id) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allIssues.length - 1;

  const goToNext = useCallback(() => {
    if (hasNext && onNavigate) {
      onNavigate(allIssues[currentIndex + 1]);
    }
  }, [hasNext, onNavigate, allIssues, currentIndex]);

  const goToPrev = useCallback(() => {
    if (hasPrev && onNavigate) {
      onNavigate(allIssues[currentIndex - 1]);
    }
  }, [hasPrev, onNavigate, allIssues, currentIndex]);

  // Build the full debugging prompt for Claude Code.
  // Copy what the user SEES: the Developer Direction textarea holds the
  // freshest direction (just typed, just saved, or edited). Building from the
  // issue prop alone copied a pre-annotation snapshot and silently dropped
  // the direction on the save-then-copy workflow (#4304).
  const buildClaudePrompt = useCallback((): string => {
    if (!issue) return "";
    const liveNotes = developerNotes.trim();
    // An unsaved edit is the signed-in user's direction (Copy & Start saves it
    // under them); a saved one keeps its stored author and date.
    const edited = developerNotes !== (issue.developerNotes || "");
    return buildCopyForClaudeText({
      ...issue,
      developerNotes: liveNotes.length > 0 ? developerNotes : undefined,
      ...(edited
        ? { developerNotesBy: signedInAsIssuePerson(user, owners), developerNotesUpdatedAt: undefined }
        : {}),
    });
  }, [issue, developerNotes, user, owners]);

  // Memoize handlers for keyboard shortcuts
  const handleApproveRef = useCallback(async () => {
    if (!issue || issue.status !== "needs_review" || isApproving) return;
    setIsApproving(true);
    try {
      // Pass the textarea's current value so unsaved direction is persisted
      // by the approve callable instead of silently discarded (#4304).
      await onApprove(issue.id, developerNotes);
      toast.success("Issue approved! Moving to next...");
      setTimeout(() => goToNext(), 300);
    } catch (err) {
      toast.error("Failed to approve issue");
    } finally {
      setIsApproving(false);
    }
  }, [issue, isApproving, onApprove, goToNext, developerNotes]);

  const handleDismissRef = useCallback(async () => {
    if (!issue || issue.status !== "needs_review" || isDismissing) return;
    setIsDismissing(true);
    try {
      onDismiss(issue.id, "not_a_bug");
      toast.success("Issue dismissed. Moving to next...");
      setTimeout(() => goToNext(), 300);
    } catch (err) {
      toast.error("Failed to dismiss issue");
    } finally {
      setIsDismissing(false);
    }
  }, [issue, isDismissing, onDismiss, goToNext]);

  const handleCopyRef = useCallback(async () => {
    if (!issue) return;
    const prompt = buildClaudePrompt();
    const { safeWriteClipboard } = await import('../../lib/utils/clipboard');
    const ok = await safeWriteClipboard(prompt);
    if (ok) {
      setCopied(true);
      toast.success("Copied to clipboard! Paste into Claude Code.");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Failed to copy to clipboard");
    }
  }, [issue, buildClaudePrompt]);

  const handleCopyAndStart = useCallback(async () => {
    if (!issue) return;
    const prompt = buildClaudePrompt();
    // Claim first: if someone else already started it, the server refuses
    // and nothing is copied (the page shows "Already started by X").
    if (onPickUp) {
      if (!(await onPickUp(issue))) return;
    } else {
      onStatusChange(issue.id, "in_progress");
    }
    // The status flip is done, so also persist any unsaved direction —
    // otherwise the clipboard carries it but the stored doc keeps stale notes.
    if (developerNotes !== (issue.developerNotes || "") && !saveNotes.isPending) {
      saveNotes.mutateAsync({ issueId: issue.id, developerNotes }).catch(() => {
        toast.error("Started, but saving the developer notes failed");
      });
    }
    const { safeWriteClipboard } = await import('../../lib/utils/clipboard');
    const ok = await safeWriteClipboard(prompt);
    if (ok) {
      setCopied(true);
      toast.success("Copied! Moved to In Progress.");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Started, but copying failed. Use Copy for Claude.");
    }
  }, [issue, buildClaudePrompt, onPickUp, onStatusChange, developerNotes, saveNotes]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input, choosing in a select (typing "d" to pick "Dillan" must not dismiss), or while a dialog is open.
      if (suspendShortcuts) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.target instanceof HTMLElement && e.target.closest('[role="dialog"]')) return;

      switch (e.key) {
        case "Escape":
          if (showImageFullscreen) {
            setShowImageFullscreen(false);
          } else {
            onClose();
          }
          break;
        case "ArrowLeft":
          goToPrev();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "a":
        case "A":
          handleApproveRef();
          break;
        case "d":
        case "D":
          handleDismissRef();
          break;
        case "c":
        case "C":
          if (issue && (issue.status === "needs_review" || issue.status === "reviewed")) {
            handleCopyAndStart();
          } else {
            handleCopyRef();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showImageFullscreen, goToNext, goToPrev, onClose, handleApproveRef, handleDismissRef, handleCopyRef, handleCopyAndStart, issue, suspendShortcuts]);

  // Early return after all hooks
  if (!issue) return null;

  const severityConfig = ISSUE_SEVERITY_CONFIG[issue.severity || "medium"];
  const statusConfig = ISSUE_STATUS_CONFIG[issue.status];
  const timeAgo = issue.createdAt?.toDate
    ? formatDistanceToNow(issue.createdAt.toDate(), { addSuffix: true })
    : "recently";

  return (
    <>
      {/* Backdrop (modal only: the page variant is the page itself) */}
      {!pageMode && (
        <div
          data-testid="issue-backdrop"
          className="fixed inset-0 bg-black/60 z-40"
          onClick={onClose}
        />
      )}

      <div
        data-testid={pageMode ? "issue-page" : "issue-modal"}
        className={
          pageMode
            ? "mx-4 my-3 h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-sm border border-slate-300 flex flex-col overflow-hidden"
            : "fixed inset-4 md:inset-8 lg:inset-12 bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
        }
      >
        {/* Header with navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-300 bg-slate-50">
          <div className="flex items-center gap-4">
            {/* Navigation arrows */}
            <div className="flex items-center gap-1">
              <button
                onClick={goToPrev}
                disabled={!hasPrev}
                className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Previous issue (←)"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-500 min-w-[60px] text-center">
                {currentIndex >= 0 ? `${currentIndex + 1} / ${allIssues.length}` : "—"}
              </span>
              <button
                onClick={goToNext}
                disabled={!hasNext}
                className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Next issue (→)"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Status badges */}
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${severityConfig.bgColor} ${severityConfig.textColor}`}>
                {severityConfig.label}
              </span>
              <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                {statusConfig.label}
              </span>
            </div>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="hidden md:flex items-center gap-4 text-xs text-slate-400">
            <span><kbd className="px-1.5 py-0.5 bg-slate-200 rounded">A</kbd> Approve</span>
            <span><kbd className="px-1.5 py-0.5 bg-slate-200 rounded">D</kbd> Dismiss</span>
            <span><kbd className="px-1.5 py-0.5 bg-slate-200 rounded">C</kbd> Copy</span>
            <span><kbd className="px-1.5 py-0.5 bg-slate-200 rounded">←</kbd><kbd className="px-1.5 py-0.5 bg-slate-200 rounded">→</kbd> Navigate</span>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
            title={pageMode ? "Back to the board (Esc)" : "Close (Esc)"}
            aria-label={pageMode ? "Back to the board" : "Close"}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Main Content - Two column layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Column - Screenshot (60% width) */}
          <div className="w-3/5 border-r border-slate-200 bg-slate-900 flex flex-col">
            {issue.screenshotUrl ? (
              <div ref={imageContainerRef} className="flex-1 relative overflow-auto p-4">
                {/* Image controls */}
                <div className="absolute top-4 right-4 z-10 flex items-center gap-2 bg-black/50 rounded-lg p-1">
                  <button
                    onClick={() => setImageZoom(z => Math.max(0.5, (z || 1) - 0.25))}
                    className="p-1.5 text-white/70 hover:text-white rounded transition-colors"
                    title="Zoom out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-white/70 text-xs min-w-[40px] text-center">{Math.round((imageZoom || 1) * 100)}%</span>
                  <button
                    onClick={() => setImageZoom(z => Math.min(3, (z || 1) + 0.25))}
                    className="p-1.5 text-white/70 hover:text-white rounded transition-colors"
                    title="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowImageFullscreen(true)}
                    className="p-1.5 text-white/70 hover:text-white rounded transition-colors"
                    title="Fullscreen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
                <img
                  ref={imageRef}
                  src={issue.screenshotUrl}
                  alt="Issue screenshot"
                  className="max-w-none cursor-zoom-in transition-transform"
                  style={{ transform: `scale(${imageZoom || 1})`, transformOrigin: 'top left' }}
                  onLoad={handleImageLoad}
                  onClick={() => setShowImageFullscreen(true)}
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Image className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No screenshot attached</p>
                </div>
              </div>
            )}
            {/* Every file and screenshot on the issue, at any stage (L-1224): add, view, remove */}
            <IssueFilesPanelView key={issue.id} issue={issue} uploader={uploader} />
          </div>

          {/* Right Column - Details (40% width) */}
          <div className="w-2/5 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Title & Meta */}
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">{issue.title}</h2>
                <div className="flex items-center flex-wrap gap-2 text-sm text-slate-500">
                  <span>{issue.reportedBy.email}</span>
                  <span>•</span>
                  <span>{timeAgo}</span>
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  <span className="font-medium">Route:</span> {issue.context?.route || "Unknown"}
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Description</h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{issue.description}</p>
              </div>

              {/* Developer Direction */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-slate-700">Developer Direction</h3>
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-indigo-100 text-indigo-700 rounded">Developer</span>
                  </div>
                  {onOwnerChange && owners ? (
                    <OwnerPicker
                      owner={issue.owner}
                      owners={owners}
                      onChange={(next) => onOwnerChange(issue.id, next)}
                    />
                  ) : (
                    <span className="text-sm text-slate-600">Owner: {issue.owner?.name ?? "Unowned"}</span>
                  )}
                </div>
                <textarea
                  value={developerNotes}
                  onChange={(e) => setDeveloperNotes(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSaveNotes();
                    }
                  }}
                  placeholder="Add direction for Claude Code: what to fix, how to approach it, files to look at..."
                  className="w-full min-h-[80px] p-3 text-sm border border-slate-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                  rows={3}
                />
                {issue.developerNotes && issue.developerNotesBy && (
                  <p className="mt-1 text-xs text-slate-500">
                    Saved by {issue.developerNotesBy.name}
                    {issue.developerNotesBy.via === "agent" ? " (via agent)" : ""}
                    {issue.developerNotesUpdatedAt?.toDate
                      ? ` · ${issue.developerNotesUpdatedAt.toDate().toLocaleString()}`
                      : ""}
                  </p>
                )}
                {issue.status === "in_progress" && issue.pickedUpBy && (
                  <p className="mt-1 text-xs text-slate-500">
                    Started in Claude by {issue.pickedUpBy.name}
                    {issue.pickedUpAt?.toDate ? ` · ${issue.pickedUpAt.toDate().toLocaleString()}` : ""}
                  </p>
                )}
                {developerNotes !== (issue?.developerNotes || "") && (
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={handleSaveNotes}
                      disabled={isSavingNotes}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition-colors"
                    >
                      {isSavingNotes ? "Saving..." : "Save Notes (Ctrl+Enter)"}
                    </button>
                  </div>
                )}
              </div>

              {/* AI Analysis */}
              {issue.analysis && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-blue-800">AI Analysis</h3>
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      issue.analysis.confidence === "high" ? "bg-green-100 text-green-700" :
                      issue.analysis.confidence === "medium" ? "bg-yellow-100 text-yellow-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {issue.analysis.confidence} confidence
                    </span>
                  </div>
                  <div className="space-y-3 text-sm text-blue-700">
                    <div>
                      <span className="font-medium">Root Cause:</span>
                      <p className="mt-1">{issue.analysis.rootCause}</p>
                    </div>
                    <div>
                      <span className="font-medium">Proposed Fix:</span>
                      <p className="mt-1">{issue.analysis.proposedApproach}</p>
                    </div>
                    {issue.analysis.affectedFiles.length > 0 && (
                      <div>
                        <span className="font-medium">Affected Files:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {issue.analysis.affectedFiles.map((file, i) => (
                            <code key={i} className="px-1.5 py-0.5 bg-blue-100 rounded text-xs">
                              {file}
                            </code>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Console Errors */}
              {issue.context?.consoleLogs && issue.context.consoleLogs.filter(l => l.level === "error").length > 0 && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <h3 className="text-sm font-medium text-red-800 mb-2">Console Errors</h3>
                  <div className="space-y-1 text-xs font-mono text-red-700 max-h-32 overflow-y-auto">
                    {issue.context.consoleLogs.filter(l => l.level === "error").map((log, i) => (
                      <div key={i} className="bg-red-100/50 rounded px-2 py-1 truncate">
                        {log.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Context */}
              {issue.context && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Environment</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div><span className="font-medium">Browser:</span> {issue.context.browser || "Unknown"}</div>
                    <div><span className="font-medium">Viewport:</span> {issue.context.viewport?.width}x{issue.context.viewport?.height}</div>
                  </div>
                </div>
              )}

              {/* Status Notes */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-slate-500" />
                  <h3 className="text-sm font-medium text-slate-700">
                    Notes{issue.statusNotes?.length ? ` (${issue.statusNotes.length})` : ""}
                  </h3>
                </div>

                {/* Existing notes - chronological (oldest first) */}
                {issue.statusNotes && issue.statusNotes.length > 0 ? (
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto" ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                    {issue.statusNotes.map((note, i) => {
                      const noteTime = note.timestamp?.toDate
                        ? formatDistanceToNow(note.timestamp.toDate(), { addSuffix: true })
                        : "just now";
                      return (
                        <div key={i} className="bg-slate-50 rounded-lg px-3 py-2">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium text-slate-700">{note.author}</span>
                            <span className="text-xs text-slate-400">{noteTime}</span>
                          </div>
                          <p className="text-xs text-slate-600 whitespace-pre-wrap">{note.text}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 mb-3">No notes yet.</p>
                )}

                {/* Add note input + file upload */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && noteText.trim()) {
                        addNote.mutate(
                          { issueId: issue.id, text: noteText.trim() },
                          {
                            onSuccess: () => setNoteText(""),
                            onError: () => toast.error("Failed to add note"),
                          }
                        );
                      }
                    }}
                    placeholder="Add a note..."
                    className="flex-1 text-sm px-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    data-testid="notes-file-input"
                    aria-label="Choose files to attach"
                    multiple
                    onChange={async (e) => {
                      const chosen = Array.from(e.target.files ?? []);
                      e.target.value = "";
                      if (chosen.length > 0) reportFileOutcomes(await uploadFiles(chosen), issue.issueCode || undefined);
                    }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30 transition-colors"
                    title="Attach file"
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Paperclip className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (!noteText.trim()) return;
                      addNote.mutate(
                        { issueId: issue.id, text: noteText.trim() },
                        {
                          onSuccess: () => setNoteText(""),
                          onError: () => toast.error("Failed to add note"),
                        }
                      );
                    }}
                    disabled={!noteText.trim() || addNote.isPending}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Add note"
                  >
                    {addNote.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                {/* Copy button - "Copy & Start" for pre-work statuses, "Copy for Claude" otherwise */}
                <button
                  onClick={
                    issue.status === "needs_review" || issue.status === "reviewed"
                      ? handleCopyAndStart
                      : handleCopyRef
                  }
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
                    copied
                      ? "bg-green-600 text-white"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5" />
                      Copied!
                    </>
                  ) : issue.status === "needs_review" || issue.status === "reviewed" ? (
                    <>
                      <Copy className="w-5 h-5" />
                      Copy &amp; Start
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      Copy for Claude
                    </>
                  )}
                </button>

                {issue.status === "needs_review" && (
                  <>
                    <button
                      onClick={handleApproveRef}
                      disabled={isApproving}
                      className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 transition-colors"
                    >
                      {isApproving ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle className="w-5 h-5" />
                      )}
                      Approve
                    </button>
                    <button
                      onClick={handleDismissRef}
                      disabled={isDismissing}
                      className="flex items-center justify-center gap-2 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      {isDismissing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                      Dismiss
                    </button>
                  </>
                )}

                {/* Status transition dropdown for non-review issues */}
                <div className="relative">
                  <button
                    onClick={() => setShowStatusMenu(!showStatusMenu)}
                    aria-haspopup="menu"
                    aria-expanded={showStatusMenu}
                    className="flex items-center justify-center gap-2 px-5 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
                  >
                    <ArrowRight className="w-5 h-5" />
                    Move to...
                    <ChevronDown className={`w-4 h-4 transition-transform ${showStatusMenu ? "rotate-180" : ""}`} />
                  </button>
                  {showStatusMenu && (
                    <div role="menu" className="absolute bottom-full right-0 mb-2 bg-white border border-slate-200 rounded-lg shadow-xl py-1 min-w-[220px] z-10">
                      {moveTargets(issue).map((transition) => (
                        <button
                          key={transition.status}
                          role="menuitem"
                          onClick={() => {
                            onStatusChange(issue.id, transition.status);
                            setShowStatusMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                            transition.status === "dismissed" ? "text-red-700 hover:bg-red-50" : transition.status === "done" ? "text-green-700 hover:bg-green-50" : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          {transition.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Image Overlay */}
      {showImageFullscreen && issue.screenshotUrl && (
        <div
          className="fixed inset-0 bg-black z-[60] flex items-center justify-center cursor-zoom-out"
          onClick={() => setShowImageFullscreen(false)}
        >
          <button
            onClick={() => setShowImageFullscreen(false)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white bg-black/50 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={issue.screenshotUrl}
            alt="Issue screenshot - fullscreen"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </>
  );
}
